import { useImperativeHandle, forwardRef } from 'react'

// Components
import { Box, Typography, Divider } from '@mui/material'

// Types
import type { ReportsDynamicFormProps, ReportsDynamicFormRef } from './types'

// Logic
import useReportsDynamicFormLogic from './ReportsDynamicForm.logic'

// Styles
import styles from './ReportsDynamicForm.module.scss'

const ReportsDynamicForm = forwardRef<
  ReportsDynamicFormRef,
  ReportsDynamicFormProps
>((props, ref) => {
  const {
    renderField,
    formDef,
    handleSubmit,
    renderPhysicianField,
    renderPhysicianModal,
    isPhysicianRequired,
  } = useReportsDynamicFormLogic(props)
  const { onSubmit } = props

  useImperativeHandle(
    ref,
    () => ({
      submitForm: (isPreview?: boolean) => {
        handleSubmit((values) => onSubmit(values, isPreview))()
      },
    }),
    [handleSubmit, onSubmit]
  )

  return (
    <>
      <Box className={styles.dynamicFormContainer}>
        {isPhysicianRequired && (
          <Box className={styles.sectionContainer}>
            <Divider sx={{ marginBottom: '20px' }} />
            <Typography variant="h3" className={styles.sectionTitle}>
              {props.template?.referralPhysicianAndSurgeonRequirement ===
              'surgeon required'
                ? 'Surgeon Information'
                : 'Physician Information'}
            </Typography>
            {renderPhysicianField()}
          </Box>
        )}

        {formDef?.sections
          .filter((section) => section.fields.length > 0)
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <Box key={section.id} className={styles.sectionContainer}>
              {formDef?.sections.length > 1 ? (
                <Typography variant="h3" className={styles.sectionTitle}>
                  {section.title}
                </Typography>
              ) : (
                <Divider sx={{ marginBottom: '20px' }} />
              )}
              {section.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className={styles.sectionDescription}
                >
                  {section.description}
                </Typography>
              )}
              <div className={styles.gridContainer}>
                {section.fields.map((field) => (
                  <div key={field.id} className={`${styles.gridItem}`}>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            </Box>
          ))}
      </Box>

      {/* Physician Add/Edit Modal */}
      {renderPhysicianModal()}
    </>
  )
})

ReportsDynamicForm.displayName = 'ReportsDynamicForm'

export default ReportsDynamicForm
